import { useCallback, useState, useRef, type FC, type FormEvent, useEffect } from 'react';
import { useDonationStore } from '../libs/useDonationStore';
import { useDonationNavigation } from '../libs/useDonationNavigation';
import { resolveDonationSettings, useDonationSettings, type DonationCMSSettings } from '../libs/useDonationSettings';
import { useDonationBlockProps } from '../DonationBlockProvider';
import { PAYMENT_TYPE, DONATION_FREQUENCY, DONATION_FORM_NAME } from '../libs/common';
import { type PaymentIconNames } from '3-components/Donations/AmountOptions/PaymentIcons';
import HTMLReactParser from 'html-react-parser';
import DonationGiftTile from '../components/DonationGIftTile';
import DonationSummaryBox from './DonationSummaryBox';
import { CREDIT_CARD_PROVIDER } from '3-components/Donations/libs/common';
import Image from '2-core-elements/Image/Image';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import InlineMessage from '3-components/InlineMessage/InlineMessage';
import AmountOptions from '3-components/Donations/AmountOptions/AmountOptions';
import FrequencyOptions from '3-components/Donations/FrequencyOptions/FrequencyOptions';
import PaymentOptions from '3-components/Donations/PaymentOptions/PaymentOptions';
import FeeCheckbox from '3-components/FeeCheckbox/FeeCheckbox';
import LargeMonthlyNudge from '3-components/LargeMonthlyNudge/LargeMonthlyNudge';
import { pushDataLayerOnFormError, pushDataLayerOnFormFieldComplete, pushDataLayerOnFormLoaded, pushDataLayerOnFormStepComplete } from '3-components/Tracker/Tracker';
import scrollToElement from 'utils/scrollToElement';

const DonationForm: FC = () => {
    const scrollToEl = useRef<HTMLDivElement>(null);

    const [amountOptions, setAmountOptions] = useState([
        {
            label: '',
            value: '',
            asDefault: false,
        }
    ]);

    const [isFormValid, setIsFormValid] = useState(true);

    const [showLargeMonthlyNudge, setShowLargeMonthlyNudge] = useState(false);

    const [feeCheckboxUserSet, setFeeCheckboxUserSet] = useState(false);

    const {
        baseURL,
        donationConfig,
        sslText,
        labels,
    } = useDonationBlockProps();

    const { setDonationData, donationData } = useDonationStore();

    const { nextStep } = useDonationNavigation();

    useEffect(() => {
        // push data for GA4 dataLayer on form loaded
        pushDataLayerOnFormLoaded({
            formName: DONATION_FORM_NAME,
            formType: 'donation',
        });
    }, []);

    // PROD: live config url
    const configUrl = `${baseURL}/${donationConfig?.dataUrl}/${donationConfig?.pageGuid}`;

    // DEV: turn this on to use local json config
    // const configUrl = `/assets/data/cms-donation.json`;

    const { isLoading: isLoadingConfig, isFetching, isError: isErrorConfig, data: cmsData } = useDonationSettings({ endpoint: configUrl }, { lang: donationConfig?.locale ? donationConfig.locale : 'en-AU' }, {
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        // refetchOnMount: true,
        select: useCallback((result: DonationCMSSettings) => {
            return resolveDonationSettings(result);
        }, []),
        onSuccess(data) {
            // console.debug(`refetch donation form`);
            // set default value for frequency and amount options;
            const defaultFrequency = data.frequencyField.options.find(opt => {
                return opt.asDefault;
            })?.value ?? 'once';

            const defaultAmount = data.amountField.options[defaultFrequency].find(opt => {
                return opt.asDefault;
            })?.value ?? '0';

            const feeSettings = data.additionalFeeSettings;
            const feeAmount = !feeCheckboxUserSet && feeSettings.enable && feeSettings.defaultChecked ? feeSettings.feeAmount : donationData.fee;

            setAmountOptions(data.amountField.options[defaultFrequency]);
            setShowLargeMonthlyNudge(data.largeMonthlyNudgeSettings.enable);
            setDonationData({
                amount: parseFloat(defaultAmount),
                frequency: defaultFrequency,
                fee: feeAmount,
            });
        },
    });

    const currency = donationConfig?.currency ?? 'AUD';
    const currencySymbol = donationConfig?.currencySymbol ?? '$';

    const onFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();

        if (!isFormValid) {
            // set focus to donation amount field
            const amountBox = document.querySelector('.amount-selection-field');
            if (amountBox) {
                scrollToElement(amountBox as HTMLElement);
            }
            return;
        }

        const { amount, frequency, fee, payment } = donationData;

        setDonationData({
            amount,
            fee,
            frequency,
            payment,
        });

        nextStep();
    };

    if (isLoadingConfig || isFetching) {
        return (
            <LoadingIndicator />
        );
    }

    if (isErrorConfig) {
        return (
            <InlineMessage status="error">
                Something went wrong, please contact administrator.
            </InlineMessage>
        );
    }

    const availablePayments = {
        once: cmsData.paymentMethods.find(opt => {
            return opt.type === DONATION_FREQUENCY.ONCE;
        })?.payments,
        monthly: cmsData.paymentMethods.find(opt => {
            return opt.type === DONATION_FREQUENCY.MONTHLY;
        })?.payments,
    };

    const { frequencyField, amountField, additionalFeeSettings: feeSettings, geoTrustSettings, largeMonthlyNudgeSettings } = cmsData;

    const frequencyOptions = frequencyField.options.map(option => {
        return {
            label: option.label,
            value: option.value,
            checked: option.asDefault,
            disabled: !option.enable,
        };
    });

    const isMonthlyOptionEnabled = !!frequencyField.options.filter(option => {
        return option.value === 'monthly' && option.enable === true;
    }).length;

    const isEnableSmallNudge = cmsData.frequencyField.smallNudgeSettings.enable && isMonthlyOptionEnabled;

    const onceAmounts = amountField.options.once.map(opt => {
        return { ...opt };
    });

    const monthlyAmounts = amountField.options.monthly.map(opt => {
        return { ...opt };
    });

    const minimumAmountSettings = {
        once: {
            value: cmsData.amountField.minimumAmount.once,
            message: labels?.onceMinimumAmountMessage ?? undefined,
        },
        monthly: {
            value: cmsData.amountField.minimumAmount.monthly,
            message: labels?.monthlyMinimumAmountMessage ?? undefined,
        },
    };

    /**
     * Payment icons that will be shown under donation amount options
     */
    const paymentIconsOnce: PaymentIconNames[] = [];
    const paymentIconsMonthly: PaymentIconNames[] = [];

    if (cmsData.paymentMethods) {
        availablePayments?.once?.forEach(payment => {
            const isPaypalAvailable = payment.type === PAYMENT_TYPE.PAYPAL && payment.enable;
            const isCreditCardAvailable = payment.enable && payment.type === PAYMENT_TYPE.CREDIT_CARD;
            const isHongkongCreditCardAvailable = payment.enable && payment.provider === CREDIT_CARD_PROVIDER.IPG;

            if (isPaypalAvailable) {
                paymentIconsOnce.push('paypal');
            }

            if (isCreditCardAvailable) {
                paymentIconsOnce.push('mastercard', 'visa', 'amex');
            }

            if (isHongkongCreditCardAvailable) {
                paymentIconsOnce.push('unionpay');
            }
        });

        availablePayments?.monthly?.forEach(payment => {
            const isCreditCardAvailable = payment.enable && payment.type === PAYMENT_TYPE.CREDIT_CARD;
            const isHongkongCreditCardAvailable = payment.enable && payment.provider === CREDIT_CARD_PROVIDER.IPG;

            if (isCreditCardAvailable) {
                paymentIconsMonthly.push('mastercard', 'visa', 'amex');
            }

            if (isHongkongCreditCardAvailable) {
                paymentIconsMonthly.push('unionpay');
            }
        });
    }

    const paymentIcons = {
        once: paymentIconsOnce,
        monthly: paymentIconsMonthly,
    };

    return (
        <div className="donation-form-wrapper" ref={scrollToEl}>
            <form onSubmit={onFormSubmit}>
                <div className="donation-form-layout">
                    <div className="donation-form-col donation-fields">
                        <FrequencyOptions
                            fieldLabel={cmsData.frequencyField.label}
                            fieldName="donation_type"
                            options={frequencyOptions}
                            selectedVal={donationData.frequency}
                            enableNudge={isEnableSmallNudge}
                            nudgeLabel={cmsData.frequencyField.smallNudgeSettings.label}
                            monthlyNudgeLabel={cmsData.frequencyField.smallNudgeSettings.monthlyLabel}
                            onChange={(value): void => {
                                setDonationData({ frequency: value });

                                if (value === DONATION_FREQUENCY.ONCE) {
                                    setAmountOptions(onceAmounts);
                                    if (largeMonthlyNudgeSettings.enable) {
                                        setShowLargeMonthlyNudge(true);
                                    }

                                    const amountOfFrequency = onceAmounts.find(option => {
                                        return option.asDefault;
                                    })?.value ?? '0';

                                    // set form value
                                    setDonationData({ amount: parseFloat(amountOfFrequency) });
                                } else {
                                    setAmountOptions(monthlyAmounts);

                                    const amountOfFrequency = monthlyAmounts.find(option => {
                                        return option.asDefault;
                                    })?.value ?? '0';
                                    // set form value
                                    setDonationData({ amount: parseFloat(amountOfFrequency) });
                                }

                                // GA4 dataLayer
                                pushDataLayerOnFormFieldComplete({
                                    formName: DONATION_FORM_NAME,
                                    fieldName: 'donation_type',
                                    formType: 'donation',
                                    value,
                                });
                            }}
                        />

                        <div className="amount-selection-field amounts-selection-wrapper">
                            <AmountOptions
                                fieldLabel={amountField.label}
                                fieldName="amount"
                                customAmountLabel={amountField.customLabel}
                                customAmountFieldName="custom_amount"
                                currencyCode={currency}
                                currencySymbol={currencySymbol}
                                options={amountOptions}
                                minimumAmount={minimumAmountSettings[donationData.frequency]}
                                donationType={donationData.frequency}
                                onChange={(value, isError): void => {
                                    if (isError) {
                                        setIsFormValid(false);
                                        pushDataLayerOnFormError({
                                            formName: DONATION_FORM_NAME,
                                            formType: 'donation',
                                            errorMessage: 'Invalid donation amount',
                                        });
                                        return;
                                    }

                                    setDonationData({ amount: parseFloat(value) });
                                    setIsFormValid(true);

                                    // GA4 dataLayer
                                    pushDataLayerOnFormFieldComplete({
                                        formName: DONATION_FORM_NAME,
                                        fieldName: 'amount',
                                        formType: 'donation',
                                        value,
                                    });
                                }}
                                paymentIconLabel={amountField.paymentIconLabel}
                                paymentIcons={paymentIcons[donationData.frequency]}
                            />
                        </div>

                        {feeSettings.enable && (
                            <FeeCheckbox
                                feeAmount={feeSettings.feeAmount.toString()}
                                label={feeSettings.label}
                                message={feeSettings.message}
                                className="donation-fee-wrapper"
                                isChecked={donationData.fee > 0}
                                onChange={(isChecked): void => {
                                    const donationFee = isChecked ? feeSettings.feeAmount.toString() : '0';
                                    setDonationData({ fee: parseFloat(donationFee) });
                                    setFeeCheckboxUserSet(true);

                                    // GA4 dataLayer
                                    pushDataLayerOnFormFieldComplete({
                                        formName: DONATION_FORM_NAME,
                                        fieldName: 'donation_fee_included',
                                        formType: 'donation',
                                        value: isChecked ? 'yes' : 'no',
                                    });
                                }}
                            />
                        )}
                    </div>
                    <div className="donation-form-col">
                        <DonationGiftTile showImage={true} />
                    </div>
                </div>

                <div className="payment-options-container">
                    <div className="payment-options-wrapper">
                        {/**
                         * show large monthly nudge if all the conditions below are met:
                         * - enabled on cms
                         * - 'once' is selected as donation frequency
                         * - 'monthly' donation option is enabled
                         */}
                        {showLargeMonthlyNudge && donationData.frequency === DONATION_FREQUENCY.ONCE && isMonthlyOptionEnabled && (
                            <div className="donation-monthly-nudge-wrapper">
                                <LargeMonthlyNudge
                                    callToAction={largeMonthlyNudgeSettings.message}
                                    buttonText={largeMonthlyNudgeSettings.buttonLabel}
                                    buttonOnClick={(): void => {
                                        setDonationData({ frequency: DONATION_FREQUENCY.MONTHLY });

                                        setShowLargeMonthlyNudge(false);
                                        if (scrollToEl.current) {
                                            scrollToElement(scrollToEl.current);
                                        }
                                    }}
                                />
                            </div>
                        )}

                        {availablePayments[donationData.frequency] && (
                            <div className="donate-submit-wrapper">
                                <DonationSummaryBox>
                                    <PaymentOptions
                                        options={availablePayments[donationData.frequency] || []}
                                        onSelect={(value): void => {
                                            setDonationData({ payment: value });

                                            // GA4 dataLayer => step complete
                                            pushDataLayerOnFormStepComplete({
                                                formName: DONATION_FORM_NAME,
                                                formType: 'donation',
                                                stepName: 'amount',
                                            });
                                        }}
                                        sslMessage={sslText}
                                    />
                                </DonationSummaryBox>

                            </div>
                        )}
                    </div>
                </div>
            </form>

            {geoTrustSettings.enable && (
                <div className="donation-form-ssl-wrapper">
                    <div className="donation-form-ssl-message">
                        <div className="text-content">
                            {HTMLReactParser(geoTrustSettings.description)}
                        </div>
                        {geoTrustSettings.privacyButton.buttonLabel && (
                            <div className="readmore-link">
                                <a href={geoTrustSettings.privacyButton.link} className="link-skin-1 link-size-md" target={geoTrustSettings.privacyButton.openNewTab ? '_blank' : '_self'} rel="noreferrer" ><span className="link-text">{geoTrustSettings.privacyButton.buttonLabel}</span></a>
                            </div>
                        )}
                    </div>

                    {geoTrustSettings.imageURL && (
                        <div className="donation-form-ssl-image">
                            <Image
                                imgSrc={baseURL + geoTrustSettings.imageURL}
                                imgAlt="GeoTrust SSL Logo"
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DonationForm;
